'use client';

import { useTranslations } from 'next-intl';
import type { EditableNavigationMenuItemProps } from '../../../NavigationMenu/components/NavigationMenuItem/types';
import { NavigationMenu } from '../../../NavigationMenu/NavigationMenu';

type AccountAccessSectionProps = {
  namespace: string;
  className?: string;
  user: {
    mobileNumber?: string;
    hasMobileNumber?: boolean;
    email?: string;
    isLoading?: boolean;
    isMobileVerified?: boolean;
    isEmailVerified?: boolean;
  };
  onEditMobile: () => void;
  onEditEmail: () => void;
  onVerifyMobile?: () => void;
  onVerifyEmail?: () => void;
};

function AccountAccessSection({ namespace, className, user, onEditMobile, onEditEmail, onVerifyMobile, onVerifyEmail }: AccountAccessSectionProps) {
  const t = useTranslations(namespace);
  const hasMobileNumber = user.hasMobileNumber ?? Boolean(user.mobileNumber);

  const mobileDisplayData = (() => {
    if (user.isLoading) {
      return ' ';
    }

    if (hasMobileNumber) {
      return user.mobileNumber ?? '';
    }

    return <span className="text-foreground-subtle font-normal">{t('account_access.mobile_number_not_added')}</span>;
  })();

  const accountAccessItems: EditableNavigationMenuItemProps[] = [
    {
      label: t('account_access.mobile_number'),
      data: mobileDisplayData,
      buttonText: hasMobileNumber ? t('edit') : t('add'),
      buttonTrackName: hasMobileNumber ? 'Edit' : 'Add',
      chevron: false,
      onClick: onEditMobile,
      onVerify: hasMobileNumber ? onVerifyMobile : undefined,
      verified: user.isLoading ? true : !hasMobileNumber || (user.isMobileVerified ?? false),
      namespace: 'my_account.navigation_menu',
      variant: 'editable',
      isLoading: user.isLoading,
      'data-track-privacy': 'Mask',
      'data-track-container': 'Mobile',
      'data-testid': 'mobile-edit-section',
      'button-data-testid': hasMobileNumber ? 'mobile-edit-section-button' : 'mobile-add-section-button',
    },
    {
      label: t('account_access.email'),
      data: user.email ?? '',
      buttonText: t('edit'),
      chevron: false,
      onClick: onEditEmail,
      onVerify: onVerifyEmail,
      verified: user.isLoading ? true : (user.isEmailVerified ?? false),
      namespace: 'my_account.navigation_menu',
      variant: 'editable',
      isLoading: user.isLoading,
      'data-track-privacy': 'Mask',
      'data-track-container': 'Email',
      'data-testid': 'email-edit-section',
      'button-data-testid': 'email-edit-section-button',
    },
  ];

  return (
    <div className="flex flex-col gap-4" data-track-section="Account Access">
      <p className="paragraph-strong">{t('account_access.title')}</p>
      <NavigationMenu variant="secondary" items={accountAccessItems} className={className} />
    </div>
  );
}

export { AccountAccessSection };
